import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomSupplierSplitView from './CustomSupplierSplitView';

const supplierTrackingLabels = getLabels('Supplier');
const supplierTrackingBase = createTrackingConfig('Supplier');

export const supplierConfig = {
  entityName: 'Supplier',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 8, // Supplier entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/supplier',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      // No lookups for base supplier entity currently
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/supplier'
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth // From app config: '60%'
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality for suppliers
    masterEntityTypeId: 8, // Supplier master entity type ID for audit logs
  },

  // Custom Split View Panel configuration for Supplier module
  customSplitViewPanel: {
    enabled: true,
    component: CustomSupplierSplitView  // Use the custom Supplier split view component
  },
  
  // Custom handlers for Supplier-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in supplierPage
    onDelete: null, // Will be defined in supplierPage
    onLoad: null, // For any special loading logic
    onFormOpen: null, // Will be defined in supplierPage
    
    // Supplier-specific actions
    onActivate: null, // Will handle supplier activation
    onDeactivate: null, // Will handle supplier deactivation
    onReview: null, // Will handle supplier review process
    
    // Board functionality (if timeline/board views are needed)
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      await fetch(`/supplier/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: toGroup === 'active' }),
      });
      // Optionally refresh list afterward
    }
  },

  // Service configuration (reference only for generic service mapping)
  service: {
    name: 'supplierService',
    methods: {
      getAll: 'getAllSuppliers',
      getById: 'getSupplierById',
      create: 'createSupplier',
      update: 'updateSupplier',
      delete: 'deleteSupplier',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'Supplier Management',
  description: 'Comprehensive supplier management system for vendor relationships and compliance tracking',
  icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',

  // Fields to remove from forms before submission (relations/computed)
  fieldsToRemove: [
    'createdBy', 'updatedBy', 'createdAt', 'updatedAt', 'createdById', 'updatedById', 'fileAttachments', 'fileAttachmentCount'
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter name',
      table: { header: 'Name', width: '220px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
        message: 'Name is required and must be 2-255 characters'
      }
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text',
      placeholder: 'Enter category (e.g., Materials, Services)',
      table: { header: 'Category', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 255
      }
    },
    {
      name: 'vatNumber',
      label: 'VAT Number',
      type: 'text',
      placeholder: 'Enter VAT number',
      table: { header: 'VAT Number', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 50
      }
    },
    {
      name: 'products',
      label: 'Products/Services',
      type: 'textarea',
      placeholder: 'List products or services provided',
      rows: 3,
      table: { header: 'Products/Services', width: '300px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000
      }
    },
    {
      name: 'contactEmail',
      label: 'Contact Email',
      type: 'text',
      placeholder: 'name@example.com',
      table: { header: 'Email', width: '200px', sortable: true },
      filterable: true,
      validation: {
        pattern: /^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/, // allow empty or valid email
        message: 'Email must be valid if provided'
      }
    },
    {
      name: 'contactPhone',
      label: 'Contact Phone',
      type: 'text',
      placeholder: '+1 555 555 5555',
      table: { header: 'Phone', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 50
      }
    },
    {
      name: 'status',
      label: 'Active',
      type: 'switch',
      required: true,
      defaultValue: true,
      switchLabel: 'Active',
      description: 'Marks whether the entity is currently active',
      labelPosition: 'left',
      table: {
        header: 'Active',
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        required: true
      }
    },
    {
      name: 'lastReviewDate',
      label: 'Last Review Date',
      type: 'date',
      table: {
        header: 'Last Review',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    }
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    return columns;
  },

  exportFileName: 'qms_entities',

  // Backend field mappings (nested display fields if any)
  fieldMappings: {
    // none required for base supplier fields
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    status: [true, false], // Active suppliers first
    name: 'asc' // Alphabetical by default
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'name', 'category', 'vatNumber', 'status'
      ]
    },
    {
      name: 'contact',
      label: 'Contact',
      icon: '☎️',
      fields: [
        'contactEmail', 'contactPhone'
      ]
    },
    {
      name: 'review',
      label: 'Review & Status',
      icon: '✅',
      fields: [
        'lastReviewDate'
      ]
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['name', 'category', 'vatNumber', 'products'],
      fieldSpans: {
        'name': 'md:col-span-6',
        'category': 'md:col-span-6',
        'vatNumber': 'md:col-span-6',
        'products': 'md:col-span-12'
      }
    },
    {
      title: 'Contact',
      icon: '☎️',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['contactEmail', 'contactPhone']
    },
    {
      title: 'Status & Review',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['status', 'lastReviewDate']
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
  ],

  // Tracking — show audit users by display name (nested objects from API / selected row)
  trackingSection: {
    ...supplierTrackingBase,
    fields: supplierTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: supplierTrackingLabels.createdDateLabel,
            userEmptyText: supplierTrackingLabels.notSpecifiedText,
            dateEmptyText: supplierTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: supplierTrackingLabels.updatedDateLabel,
              userEmptyText: supplierTrackingLabels.notModifiedText,
              dateEmptyText: supplierTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Supplier labels + custom overrides)
  labels: {
    // Using predefined Supplier labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Supplier',
    plural: 'Suppliers',
    pageTitle: 'Supplier Management',
    pageSubtitle: 'Comprehensive supplier management for vendor relationships and compliance',
    searchPlaceholder: 'Search suppliers by name, category, or contact...',
    viewTitle: 'View Supplier',
    noSelectionText: 'Select a supplier to see details',
    
    // Action labels
    activateSupplier: 'Activate Supplier',
    deactivateSupplier: 'Deactivate Supplier',
    reviewSupplier: 'Review Supplier',
    contactSupplier: 'Contact Supplier'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Ensure boolean for status
    if (transformed.status !== undefined) {
      if (transformed.status === '' || transformed.status === null) {
        delete transformed.status;
      } else if (typeof transformed.status === 'string') {
        transformed.status = transformed.status === 'true';
      } else {
        transformed.status = Boolean(transformed.status);
      }
    }

    // Convert date fields
    const dateFields = ['lastReviewDate'];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    return transformed;
  },

  // Notification messages
  messages: { 
    loading: 'Loading entities...',
    noData: 'No entities found. Create your first entity to get started.',
    loadError: 'Failed to load entities',
    saveSuccess: 'Entity saved successfully!',
    saveError: 'Failed to save entity',
    deleteSuccess: 'Entity deleted successfully!',
    deleteError: 'Failed to delete entity',
    deleteConfirm: 'Are you sure you want to delete this entity? This action cannot be undone and will remove all associated data.',
    activateSuccess: 'Entity activated successfully!',
    deactivateSuccess: 'Entity deactivated successfully!',
    reviewSuccess: 'Entity review updated successfully!'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    name: { short: 'Official name of the entity or vendor company.' },
    category: { short: 'Category or type of entity (Materials, Services, Equipment, etc.).' },
    vatNumber: { short: 'VAT / tax identification number for the supplier.' },
    products: { short: 'List of products or services provided by this entity.' },
    contactEmail: { short: 'Primary email address for contacting the entity.' },
    contactPhone: { short: 'Primary phone number for contacting the entity.' },
    status: { short: 'Whether the entity is currently active and available for business.' },
    lastReviewDate: { short: 'Date when the entity was last reviewed for performance and compliance.' },
    fileAttachments: { short: 'Supporting documents, contracts, certificates, or related files for this entity.' }
  }
};

export default supplierConfig;

